import { ImageWithFocalPointFieldsFragment, VideoFieldsFragment, VideoJsFieldsFragment } from 'types/generated';
import { ImageWithFocalPoint } from '../ImageWithFocalPoint';
import clsx from 'clsx';
import { BREAKPOINTS } from '@/config/constants';
import VideoPlayer from '../VideoPlayer';
import dynamic from 'next/dynamic';
import * as React from 'react';
const VideoJSPlayer = dynamic(() => import('@/components/VideoJS/VideoPlayer'), { ssr: false });

type PropsMobile = {
  bannerType: 'edge' | 'heroHomePage' | 'heroSubPage';
  isImageFade: boolean | null | undefined;
  image?: ImageWithFocalPointFieldsFragment | null;
  video?: VideoFieldsFragment | VideoJsFieldsFragment | null;
  media?: {
    title?: string | null;
    description?: string | null;
    contentType?: string | null;
    fileName?: string | null;
    size?: number | null;
    url?: string | null;
    width?: number | null;
    height?: number | null;
  } | null;
  priority?: boolean;
};

type PropsDesktop = {
  imagePosition: 'left' | 'right';
  bannerType: 'edge' | 'heroHomePage' | 'heroSubPage';
  isImageFade: boolean | null | undefined;
  image?: ImageWithFocalPointFieldsFragment | null;
  video?: VideoFieldsFragment | VideoJsFieldsFragment | null;
  media?: {
    title?: string | null;
    description?: string | null;
    contentType?: string | null;
    fileName?: string | null;
    size?: number | null;
    url?: string | null;
    width?: number | null;
    height?: number | null;
  } | null;
  priority?: boolean;
};

const Video = ({ video }: { video?: VideoFieldsFragment | VideoJsFieldsFragment | null }) => {
  if (video && video.__typename === 'Video') {
    return (
      <div className="h-full overflow-hidden rounded-28">
        <VideoPlayer videoId={video.youtubeId} autoPlay={video.autoplay} muted={video.muted} loop={video.loop} />
      </div>
    );
  } else if (video && video.__typename === 'VideoJs' && video.mediaFileUrl) {
    return (
      <div className="h-full overflow-hidden rounded-28">
        <VideoJSPlayer options={{ sources: [{ src: video.mediaFileUrl, type: 'application/x-mpegURL' }], ...video }} />
      </div>
    );
  } else {
    return;
  }
};
const ImageMobile = ({ image, bannerType, priority, video }: PropsMobile) => {
  if (!image && !video) {
    return;
  }
  return (
    <>
      {image && !video && (
        <div className={clsx('relative h-[272px] object-cover md:hidden md:h-[328px]')}>
          <ImageWithFocalPoint
            image={image}
            sectionId={image.id}
            className={clsx({
              'rounded-t-28': bannerType !== 'edge',
            })}
            priority={priority}
            sizes={{ default: `${BREAKPOINTS.md}px` }}
          />
        </div>
      )}
      <div className={clsx('relative object-cover md:hidden')}>{video && <Video video={video} />}</div>
    </>
  );
};

const Media = ({ url, direction = 'right' }: { url: string; direction?: string }) => {
  return (
    <div
      style={{
        mask: `linear-gradient(to ${direction}, transparent 0%, black 100%) ${
          direction === 'right' ? 'left' : 'right'
        }`,
        maskRepeat: 'no-repeat',
      }}
    >
      <video
        playsInline
        autoPlay
        loop
        muted
        disablePictureInPicture
        id="nav-media"
        style={{
          minWidth: '100%',
          minHeight: '100%',
          zIndex: 1,
          objectFit: 'cover',
          width: '100%',
          height: 'auto',
          top: '50%',
          transform: ' translateY(-50%)',
          position: 'absolute',
        }}
      >
        <source src={url || ''} type="video/mp4" />
      </video>
      <div className="relative z-10 flex h-full min-w-[340px] max-w-[380px] flex-col items-start gap-sm self-stretch bg-transparent"></div>
    </div>
  );
};

const ImageDesktop = ({
  imagePosition,
  bannerType,
  isImageFade = false,
  image,
  priority,
  video,
  media,
}: PropsDesktop) => {
  if (!image && !video && !media) {
    return;
  } else if (bannerType === 'edge') {
    return (
      <div
        className={clsx(
          'relative col-span-7 hidden min-h-[508px] overflow-hidden object-cover md:grid xl:min-h-[708px]',
          {
            'col-start-1': imagePosition === 'left',
            'col-start-8': imagePosition === 'right',
          }
        )}
        style={
          isImageFade
            ? {
                mask: clsx(`linear-gradient(to ${imagePosition}, transparent 0%, black 100%)`, {
                  left: imagePosition === 'left',
                  right: imagePosition === 'right',
                }),
                maskRepeat: 'no-repeat',
              }
            : {}
        }
      >
        {image && !video && (
          <ImageWithFocalPoint
            image={image}
            sectionId={image.id}
            className={clsx({
              'rounded-r-8': imagePosition === 'left',
              'rounded-l-8': imagePosition === 'right',
            })}
            priority={priority}
            // From md onwards, size is at most 50% of screen
            sizes={{ breakpoints: { md: '50vw' } }}
          />
        )}
        {video && !media && <Video video={video} />}
        {media && media.url && <Media url={media.url} direction={imagePosition} />}
      </div>
    );
  } else if (bannerType === 'heroHomePage') {
    return (
      <div
        className={
          'relative col-span-6 overflow-hidden object-cover max-[767px]:hidden md:grid ' +
          `${clsx({ 'col-start-1': imagePosition === 'left', 'col-start-7': imagePosition === 'right' })} ` +
          `${isImageFade ? 'mask-l-from-50% mask-l-to-90%' : ''}`
        }
      >
        {image && !video && (
          <ImageWithFocalPoint
            image={image}
            sectionId={image.id}
            className={clsx({
              'rounded-l-8': imagePosition === 'left',
              'rounded-r-8': imagePosition === 'right',
            })}
            priority={priority}
            width={600}
            height={645}
          />
        )}
        {video && !media && <Video video={video} />}
        {media && media.url && <Media url={media.url} direction={imagePosition} />}
      </div>
    );
  } else if (bannerType === 'heroSubPage') {
    return (
      <div
        className={clsx('relative col-span-5 overflow-hidden object-cover max-[767px]:hidden md:grid', {
          'col-start-1': imagePosition === 'left',
          'col-start-6': imagePosition === 'right',
          'mask-l-from-50% mask-l-to-90%': isImageFade,
        })}
      >
        {image && !video && (
          <ImageWithFocalPoint
            image={image}
            sectionId={image.id}
            className="rounded-28"
            priority={priority}
            width={496}
            height={496}
          />
        )}
        {video && !media && <Video video={video} />}
        {media && media.url && <Media url={media.url} direction={imagePosition} />}
      </div>
    );
  } else return;
};

export { ImageMobile };
export default ImageDesktop;
